import React, { useState } from 'react';
import { Row, Col } from 'reactstrap';
import ReactHtmlParser from 'react-html-parser';
import { useIntl } from 'react-intl';
import { IImageBlock, IXCMSImage, Alignment } from '@whitelabel/helpers/types';
import { transformLink } from '../../../helpers/transform';
import { StyledTitle } from '../HeadingBlock/styledHeadingBlock';
import ButtonsBlock from '../ButtonsBlock';
import TrustPilot from '../../TrustPilot';
import {
  StyledHeroBlock,
  StyledWrapperContainer,
  StyledLeadInfoContainer,
  StyledNonMobileBGContainer,
  StyledMobileBGContainer,
  StyledImagesBlock,
  StyledTitleContainer,
  StyledCol,
  StyledWrapper,
  StyledLeadText,
  StyledAdditionalComponentWrapper,
  StyledCTABlock,
  StyledRow,
  StyledWithAdditionalWrapper,
  StyleTitleItem,
  StyledLeadInfoItem,
  StyledWithAdditionalItem,
} from './styledHeroBlock';
import DropdownLinkBlock from './DropdownLinksBlock';
import ConnectedGeoMultiQuotes from './GeoMultiQuotes';

interface IHeroBlockProps {
  title: string;
  leadText?: string;
  backgroundImage?: IXCMSImage;
  supportingImages?: {
    alignment?: Alignment;
    images?: IImageBlock[];
  };
  /*
    (ts-migrate) TODO: Migrate the remaining prop types
    ...contentBlockPropTypes
    */
  ctaOptions: any[];
  className?: string;
  headingWidth?: string;
  additionalComponent?: {
    id: string;
    type: string;
    value?: {
      component?: string;
      width?: string;
    };
  }[];
  id?: string;
  trustpilotCounter?: string;
  quotesLoading?: boolean;
  selectedQuoteIndex?: number;
  multiQuotes?: {
    id: string;
    totalPrice: number;
    pdsUrl: string;
    securityToken: string;
  }[];
  createMultiQuotes?: (...args: any[]) => any;
  selectQuote?: (...args: any[]) => any;
}

const HeroBlock = ({
  title,
  leadText = '',
  backgroundImage,
  supportingImages,
  ctaOptions,
  className,
  id = '',
  headingWidth = '7',
  additionalComponent = [],
  trustpilotCounter,
  createMultiQuotes,
  selectQuote,
  multiQuotes,
  quotesLoading,
  selectedQuoteIndex,
}: IHeroBlockProps): JSX.Element => {
  const [userEnteredPostcode, setUserEnteredPostcode] = useState('');
  const intl = useIntl();
  const hasAdditionalComponent = additionalComponent && additionalComponent.length > 0;
  const bgImageForMobile = backgroundImage?.fileForMobile || backgroundImage?.file;
  const bgImageForTablet = backgroundImage?.fileForTablet || backgroundImage?.file;
  const bgImageForDesktop = backgroundImage?.file;

  const renderTitle = () => (
    <StyledTitle>
      <span>{ReactHtmlParser(title, { transform: transformLink(intl) })}</span>
    </StyledTitle>
  );

  const renderLeadText = () => (
    <StyledLeadText>{ReactHtmlParser(leadText, { transform: transformLink(intl) })}</StyledLeadText>
  );

  const renderCTAOptions = () => (
    <StyledWrapper>
      {ctaOptions.map(({ type, value }: any) =>
        type === 'dropdown_links' ? (
          <DropdownLinkBlock key={type} {...value} />
        ) : (
          <ButtonsBlock key={type} {...value} />
        ),
      )}
    </StyledWrapper>
  );

  const renderAdditionalComponent = () => {
    const component = additionalComponent[0].value?.component;
    switch (component) {
      case 'multi_quotes': {
        if (
          createMultiQuotes === undefined ||
          quotesLoading === undefined ||
          selectQuote === undefined ||
          selectedQuoteIndex === undefined
        ) {
          // TODO add better error message or only display it in development mode
          throw new Error('not provider props');
        }
        return (
          <StyledAdditionalComponentWrapper>
            <ConnectedGeoMultiQuotes
              createMultiQuotes={createMultiQuotes}
              quotesLoading={quotesLoading}
              selectQuote={selectQuote}
              selectedQuoteIndex={selectedQuoteIndex}
              multiQuotes={multiQuotes}
              userEnteredPostcode={userEnteredPostcode}
              setUserEnteredPostcode={setUserEnteredPostcode}
            />
          </StyledAdditionalComponentWrapper>
        );
      }
      default:
        return null;
    }
  };

  const renderImagesBlock = () =>
    supportingImages &&
    supportingImages.images &&
    !!supportingImages.images.length && <StyledImagesBlock images={supportingImages.images} />;

  const renderTrustpilot = () => {
    if (!trustpilotCounter) return null;
    return <TrustPilot trustpilotCounter={trustpilotCounter} />;
  };

  const renderOneColumnLayout = () => (
    <StyledWrapperContainer>
      <StyledRow>
        <StyledMobileBGContainer $rtl={intl.bidi} $backgroundImageUrl={bgImageForMobile}>
          <StyledTitleContainer>
            <Col sm="9">{renderTitle()}</Col>
          </StyledTitleContainer>
        </StyledMobileBGContainer>
      </StyledRow>
      <StyledLeadInfoContainer>
        <Row>
          <StyledCol sm="7">{renderLeadText()}</StyledCol>
        </Row>
        <Row>
          <StyledCol sm="8">
            {renderCTAOptions()}
            {renderImagesBlock()}
            {renderTrustpilot()}
          </StyledCol>
        </Row>
      </StyledLeadInfoContainer>
    </StyledWrapperContainer>
  );

  const renderWithAdditionalComp = () => {
    const additionalItemWidth = additionalComponent[0].value?.width;
    return (
      <StyledWithAdditionalWrapper>
        <StyleTitleItem $lg={+headingWidth} $rtl={intl.bidi} $backgroundImageUrl={bgImageForMobile}>
          <StyledTitleContainer>{renderTitle()}</StyledTitleContainer>
        </StyleTitleItem>
        <StyledLeadInfoItem $lg={+headingWidth}>
          {renderLeadText()}
          {!!ctaOptions.length && <StyledCTABlock>{renderCTAOptions()}</StyledCTABlock>}
          {renderImagesBlock()}
          {renderTrustpilot()}
        </StyledLeadInfoItem>
        <StyledWithAdditionalItem $lg={additionalItemWidth ? +additionalItemWidth : undefined}>
          {renderAdditionalComponent()}
        </StyledWithAdditionalItem>
      </StyledWithAdditionalWrapper>
    );
  };

  return (
    <StyledHeroBlock id={id} className={className}>
      <StyledNonMobileBGContainer
        $rtl={intl.bidi}
        $backgroundImageUrl={bgImageForTablet}
        $bgImageForDesktop={bgImageForDesktop}
      >
        {hasAdditionalComponent ? renderWithAdditionalComp() : renderOneColumnLayout()}
      </StyledNonMobileBGContainer>
    </StyledHeroBlock>
  );
};

export default HeroBlock;
